// ConversationEngine の realtime 実装 (spec 2026-08-29 §3.4)。
//
// 半委任: 進行と設問文はアプリが持ち、setQuestion のたびに session.update で
// instructions / tools / silence_duration_ms を差し替える。
//
// **Silero VAD は使わない** — server 側が終端を判定するので音声は常時送り、
// R2 保存用の発話区間は server の speech_started / speech_stopped で決める。

import { useCallback, useMemo, useRef, useState } from "react";
import { RealtimeClient, type RealtimeSessionPatch } from "../api/realtimeClient";
import { PcmStreamPlayer } from "../audio/pcmStreamPlayer";
import { MicPcmSource } from "../audio/micPcmSource";
import { mediaStreamManager } from "../audio/mediaStreamManager";
import { redemptionMsForQuestionType } from "../audio/endpointing";
import { SilenceTimer } from "../audio/silenceTimer";
import { TTS_PHRASES } from "../audio/ttsPhrases";
import type { Question } from "../survey/schema";
import type {
  EngineCallbacks,
  EngineState,
  NeedRepeatReason,
  SpeechEngine,
} from "../conversation/engine";
import { ANSWER_TOOLS, buildQuestionInstructions } from "../conversation/realtimeInstructions";
import {
  NAME_TOOLS,
  NARRATION_TOOLS,
  buildNameInstructions,
  buildNarrationInstructions,
} from "../conversation/preflightInstructions";
import { REALTIME_MODEL } from "../conversation/realtimeModel";

const REASONS: NeedRepeatReason[] = ["no-content", "off-topic", "unclear"];

// cascade の SilenceTimer と同じ待ち時間 (10 秒 × 2 段)。**同じにするのが要点** —
// 違えると「どちらが待たせるか」の比較が engine の差ではなく設定の差になる。
const SILENCE_ESCALATIONS = [10_000, 10_000];

/**
 * 黙っている相手に促すときの指示。**この応答にだけ効く形で渡す**
 * (session.instructions を書き換えると、促した後もずっとこの指示のままになる)。
 * 文言は cascade と同じ TTS_PHRASES から取る。
 */
const SILENCE_PROMPT = `相手がしばらく黙っています。次の文を、一字一句そのまま読み上げてください。読み上げる以外のことは何も言わないでください。

${TTS_PHRASES.silence0}`;

// 読み上げるだけの step は無音を待つ必要がないので短くてよい (発話を受けない)。
// 画面を live と共有するため RealtimePreflight から移した (spec 2026-09-14 L2。値は変えていない)
const NARRATION_SILENCE_MS = 800;
// 氏名は「考える間」を挟む人がいるので long と同じ余裕を取る。
const NAME_SILENCE_MS = 1800;

/** session.started に載せる。**?stt=openai の provider と同じ流儀で、欠落 = cascade** */
const SESSION_META: Record<string, unknown> = { engine: "realtime", realtimeModel: REALTIME_MODEL };

export function useRealtimeEngine(cb: EngineCallbacks): SpeechEngine & {
  setTask: (t: RealtimeSessionPatch) => void;
} {
  const cbRef = useRef(cb);
  cbRef.current = cb;

  const [state, setState] = useState<EngineState>("idle");
  const setPhase = useCallback((s: EngineState) => {
    setState(s);
    cbRef.current.onStateChange?.(s);
  }, []);

  const clientRef = useRef<RealtimeClient | null>(null);
  // **player は hook の初期化時に作る。** start() の中で作ると、マイク許可のタップから
  // await を挟んだ後になり、iOS の autoplay 許可の文脈が切れる (unlock が効かない)。
  const playerRef = useRef<PcmStreamPlayer | null>(null);
  if (!playerRef.current) playerRef.current = new PcmStreamPlayer();
  const micRef = useRef<MicPcmSource | null>(null);
  const questionRef = useRef<Question | null>(null);
  const pausedRef = useRef(false);
  // 発話区間のバッファ (R2 保存用)。speech_started で開き、speech_stopped で閉じる。
  const utteranceRef = useRef<Float32Array[] | null>(null);
  const silenceRef = useRef<SilenceTimer | null>(null);
  /** 最後に頼んだ応答が促しか。促しを読み終えたら無音の段階を保って数える (onResponseDone) */
  const keepSilenceLevelRef = useRef(false);

  const handleTool = useCallback(
    (c: { name: string; args: string; callId: string }) => {
      const q = questionRef.current;
      /**
       * **tool の結果は必ず返す。** モデルは function_call_output が返るまで次の応答を
       * 作らないので、返さないと会話がそこで止まる (2026-09-01 の実機確認)。
       * ただし**話し始めさせるかは別** — 次の task へ進む経路では setTask が turn を
       * 始めるため、ここで response.create まで送ると応答が二重に走る。
       * 続きを話させるのは「同じ task のまま聞き直す」need_repeat だけ。
       */
      const settle = (respond: boolean) => {
        const client = clientRef.current;
        if (!client) return;
        const output = JSON.stringify({ ok: true });
        if (respond) client.respondToTool(c.callId, output);
        else client.sendToolOutput(c.callId, output);
      };

      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(c.args) as Record<string, unknown>;
      } catch {
        // 握り潰さない。ここで throw すると WS の onmessage ごと死ぬ (spec §4)。
        cbRef.current.onError({ code: "tool-invalid", message: `不正な引数: ${c.name}` });
        // 結果は返して聞き直させる — 返さないとモデルが待ったまま会話が止まる。
        settle(true);
        return;
      }
      if (c.name === "record_answer") {
        const value = typeof parsed.value === "string" ? parsed.value : "";
        const qid = typeof parsed.question_id === "string" ? parsed.question_id : (q?.id ?? "");
        if (!value || !qid) {
          cbRef.current.onError({ code: "tool-invalid", message: "record_answer に値が無い" });
          settle(true);
          return;
        }
        settle(false);
        setPhase("listening");
        cbRef.current.onAnswerCommitted(qid, value);
        return;
      }
      if (c.name === "need_repeat") {
        const r = parsed.reason;
        const reason = REASONS.includes(r as NeedRepeatReason)
          ? (r as NeedRepeatReason)
          : "unclear";
        // 同じ設問のまま聞き直す = 呼び出し側は task を変えないので、ここで続けさせる
        settle(true);
        cbRef.current.onNeedRepeat(q?.id ?? "", reason);
        return;
      }
      if (c.name === "narration_done") {
        // 画面は「読み終えた」をエンジンの違いを知らずに受ける (live は話し終えで上げる、
        // spec 2026-09-14 L10)。次の案内は画面が narrate で頼むので、ここでは話させない
        settle(false);
        cbRef.current.onNarrationDone?.();
        return;
      }
      // 事前チェックの confirm_name など。engine は中身を解釈せず素通しし、
      // 次に何を話すかは呼び出し側が決める。
      settle(false);
      cbRef.current.onToolCall?.(c.name, parsed, c.callId);
    },
    [setPhase]
  );

  const start = useCallback(async () => {
    if (clientRef.current) return; // 冪等 (連打で二重に開かない)
    const player = playerRef.current;
    if (!player) return;
    const client = new RealtimeClient({
      onToolCall: handleTool,
      onAudioDelta: (pcm) => {
        // 段階 1 では最初のチャンクが届いた時点で ai-speaking にする。
        // 「鳴り終わった」は再生キューが空になった時刻で、正確に取るには
        // PcmStreamPlayer のポーリングが要る。体感で問題が出たら足す。
        setPhase("ai-speaking");
        player.enqueue(pcm);
      },
      onTranscript: () => {
        /* 入力の書き起こし。実測で 30 件中 4 件しか返らないので当てにしない (spec D9) */
      },
      onNarrated: (text) => cbRef.current.onNarrated?.(text),
      onUsage: (u) => cbRef.current.onUsage?.(u),
      onSpeechStarted: () => {
        // **話し始めたら計測を止める (張り直さない)。** 張り直すと話している最中に
        // カウントが進み、10 秒を超えて話すと必ず割り込む — cascade が 2026-08-05 の
        // パイロットで実測した欠陥そのもの (本編の発話の 21.8% が消えた原因)。
        silenceRef.current?.reset();
        utteranceRef.current = [];
        setPhase("user-speaking");
      },
      onSpeechStopped: () => {
        const chunks = utteranceRef.current;
        // **消さないこと。** 発話の外 (処理中・沈黙中) に届く PCM を積まないための
        // 唯一の仕掛けで、下の `utteranceRef.current?.push` が null なら何もしない形に
        // 対応している。
        //
        // **これは変異検証で「等価変異」と判定した箇所である (2026-08-30)** —
        // 消しても次の speech_started が [] に作り直すので、次の発話に混ざることはなく、
        // テストで観測できる差が無い。それでも消してはいけないのは**メモリ**の話で、
        // 沈黙が続くとその間の PCM を際限なく積む (16kHz float32 = 64 KB/秒。
        // 60 秒の沈黙で約 3.8 MB)。
        //
        // 消すときに確かめること: 「speech_stopped から次の speech_started までに
        // 届いた PCM がどこにも溜まらない」ことを、この行以外の何かが保証しているか。
        utteranceRef.current = null;
        setPhase("thinking");
        if (!chunks || chunks.length === 0) return;
        const total = chunks.reduce((n, c) => n + c.length, 0);
        const merged = new Float32Array(total);
        let at = 0;
        for (const c of chunks) {
          merged.set(c, at);
          at += c.length;
        }
        cbRef.current.onUtteranceAudio?.(merged);
      },
      onError: (e) => cbRef.current.onError({ code: "ws-failed", message: e.message }),
      onResponseDone: () => {
        // **張る契機はここ 1 つだけ** — AI が話し終えた = 相手の番が始まった時刻。
        // 発話の開始や終了で張ると、話している最中にカウントが進む (上記)。
        // **促しの応答の後は段階を保つ (resume)** — start() は段階を 0 に戻すので、促しを読み終えた
        // 後にも start() すると「聞こえていますでしょうか」(段階 0) だけが繰り返され、段階 1
        // (キーボード) に永久に届かない (cascade が 2026-07-25 に踏んだのと同じ形)
        if (keepSilenceLevelRef.current) {
          // 下ろすのは念のため (変異検証で等価と判定、2026-09-14) — 次の応答までに相手が話せば
          // reset() で段階が 0 に戻り、次の task へ移れば setTask が下ろすので、残っても結果は変わらない
          keepSilenceLevelRef.current = false;
          silenceRef.current?.resume();
        } else {
          silenceRef.current?.start();
        }
      },
    });
    clientRef.current = client;
    silenceRef.current = new SilenceTimer({
      escalations: SILENCE_ESCALATIONS,
      onEscalation: (level) => cbRef.current.onSilence?.(level),
    });
    try {
      await client.connect();
    } catch (e) {
      // **ref を戻すのが要点** — 残すと冪等ガードに弾かれて二度と繋げなくなる
      // (接続の失敗はネットワーク断で日常的に起きるので、再試行できないと詰む)。
      clientRef.current = null;
      // **player は捨てない** — unlock 済みの AudioContext を作り直すと、
      // iOS では二度と音が鳴らない (ユーザー操作の文脈をもう一度は得られない)。
      cbRef.current.onError({ code: "ws-failed", message: (e as Error).message });
      return;
    }
    const stream = await mediaStreamManager.ensureStream();
    const mic = new MicPcmSource();
    micRef.current = mic;
    mic.start(stream, (pcm) => {
      if (pausedRef.current) return;
      client.sendAudio(pcm);
      utteranceRef.current?.push(pcm);
    });
    setPhase("listening");
  }, [handleTool, setPhase]);

  /**
   * instructions / tools / 無音の長さを直に差し替える (realtime 固有の低レベル API)。
   * 事前チェックは設問ではなく step を回すのでこちらを使い、setQuestion はこの上に載る。
   */
  const setTask = useCallback((task: RealtimeSessionPatch) => {
    const client = clientRef.current;
    if (!client) return;
    // **前の task の無音計測を捨てる。** task が変われば「何を待っているか」も変わるので、
    // 残すと次の読み上げの最中に前の task の促しが発火する (チュートリアルを読んでいる
    // 途中で完了案内へ飛ぶ)。**stop であって reset ではない** — reset は
    // 「ユーザーが沈黙から回復した」意味の silence.recovery を emit するので、
    // こちらの都合で task を変えただけの回に出すと観測点の意味が壊れる。
    // 張り直すのは次の onResponseDone の仕事 (level 0 から数え直す)。
    silenceRef.current?.stop();
    keepSilenceLevelRef.current = false; // 次の task は段階 0 から数える
    client.updateSession(task);
    // **この 1 行が無いとモデルは黙ったままになる** (2026-09-01 の実機確認)。
    // session.update は設定を渡すだけで、turn_detection.create_response が効くのは
    // 「ユーザーが話し終えた後」だけ。設問の読み上げも案内もこちらから始める turn なので、
    // task を差し替えたら必ず話し始めさせる。**順序が仕様** (逆だと古い instructions で話す)。
    client.createResponse();
  }, []);

  const setQuestion = useCallback(
    (q: Question) => {
      questionRef.current = q;
      setTask({
        instructions: buildQuestionInstructions(q),
        tools: ANSWER_TOOLS,
        // 設問タイプ別の無音の長さ。cascade と同じ関数を共有する (D5)。
        silenceDurationMs: redemptionMsForQuestionType(q.type),
      });
    },
    [setTask]
  );

  /** 読み上げるだけの step の文を読ませる (以前は画面が setTask にこの 3 つを渡していた) */
  const narrate = useCallback(
    (lines: string[]) => {
      setTask({
        instructions: buildNarrationInstructions(lines),
        tools: NARRATION_TOOLS,
        silenceDurationMs: NARRATION_SILENCE_MS,
      });
    },
    [setTask]
  );

  /** 氏名を伺う (復唱して確認してから confirm_name、spec 2026-08-29 D8) */
  const askName = useCallback(() => {
    setTask({
      instructions: buildNameInstructions(),
      tools: NAME_TOOLS,
      silenceDurationMs: NAME_SILENCE_MS,
    });
  }, [setTask]);

  const stop = useCallback(() => {
    silenceRef.current?.stop();
    silenceRef.current = null;
    micRef.current?.stop();
    micRef.current = null;
    // 鳴っている音は止めるが、器は捨てない (unlock を保つ)
    playerRef.current?.stop();
    clientRef.current?.close();
    clientRef.current = null;
    utteranceRef.current = null;
    setPhase("idle");
  }, [setPhase]);

  /**
   * 黙っている相手に促す。**この応答にだけ効く指示**で渡すので、次の設問の
   * 読み上げには影響しない。呼ぶかどうかは呼び出し側が決める (原則 5 の到達性を
   * engine に依存させない)。
   */
  const promptSilence = useCallback(() => {
    keepSilenceLevelRef.current = true;
    clientRef.current?.createResponse(SILENCE_PROMPT);
  }, []);

  /**
   * ユーザー操作の文脈で音を鳴らせる状態にする。**マイク許可のタップから、
   * 最初の await より前に呼ぶこと** (spec 2026-08-29 段階 2)。
   */
  const unlock = useCallback(() => {
    playerRef.current?.unlock();
  }, []);

  const pause = useCallback(() => {
    pausedRef.current = true;
    playerRef.current?.stop();
  }, []);
  const resume = useCallback(() => {
    pausedRef.current = false;
  }, []);

  return useMemo(
    () => ({
      kind: "realtime" as const,
      sessionMeta: SESSION_META,
      start,
      stop,
      pause,
      resume,
      setQuestion,
      setTask,
      narrate,
      askName,
      unlock,
      promptSilence,
      state,
    }),
    [
      start,
      stop,
      pause,
      resume,
      setQuestion,
      setTask,
      narrate,
      askName,
      unlock,
      promptSilence,
      state,
    ]
  );
}
